import { Injectable, Injector, OnDestroy } from '@angular/core';

import { defer, fromEvent, Observable, Subscription } from 'rxjs';
import { HttpService } from 'projects/core/src/public-api';
import { Cart } from './cart';
import { Part } from 'projects/cart-tv/src/app/data/parts/part';
import { CartScan } from 'projects/cart-tv/src/app/data/cart-scan';
import { bufferCount, filter, startWith, switchMap, take, tap, timeoutWith } from 'rxjs/operators';
import { CartsStore } from 'projects/cart-tv/src/app/data/carts.store';
import { KanbanColumn } from 'projects/desktop/src/app/modules/kanban-board/data/kanban-column';
import { Cabinet } from 'projects/app-misc/src/app/modules/cabinets/data/cabinet';
import { Tray } from './tray';
import { ShortProject } from 'projects/app-misc/src/app/modules/project-misc/data/project/short-project';
import { Project } from 'projects/app-misc/src/app/modules/project-misc/data/project/project';
import { environment } from 'projects/cart-tv/src/environments/environment';
import { MiscSnackBarService } from 'projects/core/src/lib/misc/view/snackbar/misc-snackbar.service';
import { PartGroup } from 'projects/cart-tv/src/app/data/parts/part-group';


export class PartLabel {
  partId: number;
  cabinetId: number;
  jobNumber: string;
  partName: string;
}

@Injectable({
  providedIn: 'root',
})
export class CartsService extends HttpService {
  scannerSub: Subscription;

  constructor(
    private cartsStore: CartsStore,
    protected injector: Injector,
    private miscSnackBarService: MiscSnackBarService
  ) {
    super(injector);
  }

  loadCarts(): Observable<Cart[]> {
    // return this.doHttpGet<Cart[]>(`/cart/list`).pipe(
    //   tap(newCarts => {
    //     this.cartsStore.set(newCarts)
    //   })
    // )
    return this.doHttpGet<Cart[]>(`/cart/list`)
  }


  getCart(cartId: number): Observable<Cart> {
    return this.getData(`/cart/${cartId}`, Cart);
  }

  createCart(cartNumber: number, smallTraysAmount: number, tallTraysAmount): Observable<Cart> {
    return this.postData(`/cart`, {
      number: cartNumber,
      smallTraysAmount,
      tallTraysAmount
    }, Cart);
  }

  deleteCart(cartId: number): Observable<Cart> {
    return this.deleteData(`/cart/${cartId}`, Cart);
  }

  getPartsForCart(cartId: number, trayId: number): Observable<Part[]> {
    return this.getData(`/cart/${cartId}/tray/${trayId}/parts`, Part);
  }

  getFinishingParts(projectId: number): Observable<PartGroup[]> {
    return this.getData(`/parts/finishing-list?projectId=${projectId}`, PartGroup);
  }

  createPart(projectId: number, part: Part): Observable<Part> {
    return this.postData(`/parts/finishing-list/add?projectId=${projectId}`, part, Part);
  }

  deletePart(partId: number): Observable<Part> {
    return this.deleteData(`/parts/${partId}`, Part);
  }

  getSortedParts(projectId: number): Observable<Part[]> {
    return this.getData(`/parts/list/for-sorting-flatten?projectId=${projectId}`, Part);
  }

  scanPart(partLabel: PartLabel): Observable<Tray> {
    return this.putData(`/cart/load-tray/by-part`, partLabel, Tray);
  }

  loadPart(partNumber: number): Observable<Tray> {
    return this.putData(`/cart/load-tray/by-part/${partNumber}`, {}, Tray);
  }

  saveTray(cartId: number, trayId: number, cabinetIds: number[]): Observable<Tray> {
    return this.putData(`/cart/${cartId}/tray/${trayId}/cabinets`, cabinetIds, Tray);
  }

  emptyTray(cartId: number, trayId: number, cabinetIds: number[]): Observable<Tray> {
    return this.putData(`/cart/${cartId}/tray`, {}, Tray);
  }

  getProjectsListForAssembly(): Observable<Project[]> {
    return this.getData('/project/list/for-assembly', Project);
  }

  changeProject(cartId: number, project: ShortProject): Observable<Cart> {
    return this.putData(`/cart`, {
      id: cartId,
      project,
    } as Cart, Cart);
  }

  emptyCart(cartId: number): Observable<Cart> {
    return this.putData(`/cart/${cartId}/empty`, {}, Cart);
  }

  savePart(part: Part): Observable<Part> {
    return this.putData('/parts', part, Part)
  }

  savePartGroup(group: PartGroup): Observable<PartGroup> {
    return this.putData(`/parts/finishing-list/${group.id}`, group, PartGroup)
  }

  subToScanner() {
    const inputs = fromEvent(document, 'keydown') as Observable<KeyboardEvent>
    let arrWithScannedStrings: string[] = [];
    let scannedString = '';

    const buffered$ = inputs.pipe(
      take(1),
      switchMap(firstKey => inputs.pipe(
        startWith(firstKey),
        timeoutWith(environment.defaultBufferResetTime, defer(() => {
          arrWithScannedStrings = [];
          scannedString = ''
          return buffered$
        })))
      ))

    this.scannerSub = buffered$.subscribe(res => {
      const event = res as KeyboardEvent

      if(event.code === 'Semicolon') {
        arrWithScannedStrings.push(scannedString)
        scannedString = ''
      } else {
        scannedString += event.key
      }

      if(arrWithScannedStrings.length >= 4) {
        const scanLabel = new PartLabel()
        scanLabel.jobNumber = arrWithScannedStrings[0]
        scanLabel.cabinetId = parseInt(arrWithScannedStrings[1])
        scanLabel.partName = arrWithScannedStrings[2]
        scanLabel.partId = parseInt(arrWithScannedStrings[arrWithScannedStrings.length - 1])

        this.scanPartHelper(scanLabel)

        arrWithScannedStrings = []
      }
    })
  }

  unSubFromScanner() {
    this.scannerSub.unsubscribe()
  }

  scanPartHelper(partLabel: PartLabel) {
    if(!partLabel || !partLabel.jobNumber || !partLabel.partName || !partLabel.cabinetId || !partLabel.partId) {
      this.miscSnackBarService.openErrorSnackBar('Wrong Part Number')
      return
    }

    this.scanPart(partLabel).subscribe(resultOfScan => {
      resultOfScan.partLabel = partLabel
      this.cartsStore.addPart(resultOfScan)
    }, error => {
      this.miscSnackBarService.openErrorSnackBar(error.message)
    })
  }
}
